<script setup lang="ts">
import {
  PhArrowClockwise,
  PhDownloadSimple,
  PhMagnifyingGlassMinus,
  PhMagnifyingGlassPlus,
} from '@phosphor-icons/vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  alt: {
    type: String,
    default: 'bild',
  },
  fileName: {
    type: String,
    default: 'image.jpg',
  },
});

const { t } = useI18n();

const MIN_SCALE = 0.8;
const MAX_SCALE = 5;

const scale = ref(MIN_SCALE);
const translateX = ref(0);
const translateY = ref(0);
const isPanning = ref(false);
const startX = ref(0);
const startY = ref(0);
const isMaxZoomedIn = computed(() => scale.value >= MAX_SCALE);
const isMaxZoomedOut = computed(() => scale.value <= MIN_SCALE);

const imageStyle = computed(() => ({
  transform: `scale(${scale.value}) translate(${translateX.value}px, ${translateY.value}px)`,
}));

const zoomIn = () => {
  scale.value = Math.min(scale.value + 0.2, MAX_SCALE); // Maximum zoom level
};

const zoomOut = () => {
  scale.value = Math.max(scale.value - 0.2, MIN_SCALE); // Minimum zoom level
};

const resetView = () => {
  scale.value = MIN_SCALE;
  translateX.value = 0;
  translateY.value = 0;
};

const startPan = (event: MouseEvent) => {
  isPanning.value = true;
  startX.value = event.clientX - translateX.value;
  startY.value = event.clientY - translateY.value;
};

const panImage = (event: MouseEvent) => {
  if (!isPanning.value) return;
  translateX.value = event.clientX - startX.value;
  translateY.value = event.clientY - startY.value;
};

const endPan = () => {
  isPanning.value = false;
};

const downloadImage = () => {
  const link = document.createElement('a');
  link.href = props.src;
  link.download = props.fileName;
  link.click();
};
</script>

<template>
  <div class="image-viewer">
    <div class="toolbar">
      <button
        class="button is-text"
        :disabled="isMaxZoomedIn"
        @click="zoomIn"
        :tooltip-text="t('fileViewer.zoomIn')"
        data-test="zoom-in-button"
      >
        <PhMagnifyingGlassPlus :size="32" />
      </button>
      <button
        class="button is-text"
        :disabled="isMaxZoomedOut"
        @click="zoomOut"
        :tooltip-text="t('fileViewer.zoomOut')"
        data-test="zoom-out-button"
      >
        <PhMagnifyingGlassMinus :size="32" />
      </button>
      <button
        @click="resetView"
        :tooltip-text="t('fileViewer.reset')"
        data-test="reset-view-button"
      >
        <PhArrowClockwise :size="32" />
      </button>
      <button
        @click="downloadImage"
        :tooltip-text="t('fileViewer.download')"
        data-test="download-image-button"
      >
        <PhDownloadSimple :size="32" />
      </button>
    </div>

    <div
      class="image-container"
      ref="imageContainer"
      @mousedown="startPan"
      @mousemove="panImage"
      @mouseup="endPan"
      @mouseleave="endPan"
    >
      <img
        :src="src"
        :style="imageStyle"
        ref="image"
        draggable="false"
        :alt="alt"
        data-test="image"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.image-viewer {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;

  .toolbar {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;

    button {
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 8px;
      border-radius: 4px;
      transition: background 0.2s;

      &:hover {
        background: rgba(0, 0, 0, 0.1);
      }
    }
  }
  .image-container {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: grab;
    transform: 0.3s ease;

    box-shadow: inset 0 0 15px rgba(0, 0, 0, 0.2);

    img {
      user-select: none;
      max-width: 100%;
      max-height: 100%;
    }
  }
}
</style>
